import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import { FieldConfig, FormArray, FormControl, FormGenerator, FormGroup, GroupProps } from 'react-reactive-form';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation, TFunction } from 'react-i18next';
import { FormUtil } from '../FormUtil';
import { IinitialState } from '../../../reducers';
import { setTableFilter } from '../../../actions/manageJobActions';
import { Ibuilding, Ifloor, Ijob, Ilocation, Ioption, IproductInfo, ItableFiltersReducer } from '../../../models';
import find from 'lodash/find';
import omit from 'lodash/omit';
import { inventoryStatusFilterEnum, jobTypesIdEnum, workOrderStatusEnum } from '../../../models-enums';
import { getStatusOrder, shouldShowStatusFilter } from '../../manageInventory/inventoryStatusFilterUtil';
import { constants } from '../../../constants/constants';
import HideVirtualMobileToggle from '../../manageInventory/HideVirtualMobileToggle';
import MultiSelectFilterList from '../MultiSelectFilterList';
import { IsimpleOption, sortOptionsNatural } from '../multiSelectFilterUtil';

interface Iprops {
    tableFilters: ItableFiltersReducer;
    t: TFunction;
    show: boolean;
    closeFilterAssetModal: any;
    bgColor: string;
    brColor: string;
    primaryColor: string;
    setTableFilter: typeof setTableFilter;
    buildings: Ibuilding[];
    selectedJob: Ijob;
    productInfo: IproductInfo;
    clearTableFilter: () => void;
    isWorkOrderTypeJob: boolean;
    isAddWorkOrderMode: boolean;
    isJobDownloaded: boolean;
}
interface Istate {
    fieldConfig: FieldConfig;
}

const FilterAssetTableModal: React.FC<Iprops & WithTranslation> = props => {
    const initialFormGroup = new FormGroup({
        building: new FormControl(''),
        floor: new FormControl(''),
        location: new FormControl(''),
        measurementPointListResultStatus: new FormControl(''),
        workOrderResultStatus: new FormControl(''),
        originalMeasurementPointListResultStatus: new FormControl(''),
        mainCategory: new FormControl('')
    });

    const [searchForm, setSearchForm] = useState<FormGroup | FormArray>(initialFormGroup);

    const [show, setShow] = useState(false);
    const [state, setState] = React.useState<Istate>({
        fieldConfig: { controls: {} }
    });

    // Room is a staged multi-select managed outside react-reactive-form; it commits
    // with the modal's Apply button (see applyFilter), matching the desktop dropdown.
    const [roomSelections, setRoomSelections] = useState<IsimpleOption[]>([]);
    const [roomOptions, setRoomOptions] = useState<Ioption[]>([]);

    const defaultBuildingID = { value: 'unallocated', label: 'Unallocated' };

    const computeRoomOptions = (buildingId?: string, floorId?: string, locationId?: string): Ioption[] => {
        if (!buildingId || !floorId || !locationId) {
            return [];
        }
        const foundBuilding = find(props.buildings, (b: Ibuilding) => b.id === buildingId);
        if (!foundBuilding || !foundBuilding.floors) {
            return [];
        }
        const foundFloor = find(foundBuilding.floors, (f: Ifloor) => f.id === floorId);
        if (!foundFloor || !foundFloor.locations) {
            return [];
        }
        const foundLocation = find(foundFloor.locations, (l: Ilocation) => l.id === locationId);
        if (!foundLocation || !foundLocation.rooms) {
            return [];
        }
        return sortOptionsNatural(FormUtil.convertToOptions(foundLocation.rooms));
    };

    const handleClose = () => {
        setShow(false);
        props.closeFilterAssetModal();
    };

    const applyFilter = () => {
        setShow(false);
        props.closeFilterAssetModal();
        let filter: any = {
            page: 0,
            floor: null,
            location: null,
            rooms: roomSelections.map(r => ({ value: r.value, label: r.label })),
            building: null,
            measurementPointListResultStatus: null,
            workOrderResultStatus: null,
            mainCategory: null,
            originalMeasurementPointListResultStatus: null
        };

        if (searchForm && searchForm.value && searchForm.value.workOrderResultStatus) {
            filter = {
                ...filter,
                workOrderResultStatus: {
                    value: searchForm.value.workOrderResultStatus.value,
                    label: searchForm.value.workOrderResultStatus.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.measurementPointListResultStatus) {
            filter = {
                ...filter,
                measurementPointListResultStatus: {
                    value: searchForm.value.measurementPointListResultStatus.value,
                    label: searchForm.value.measurementPointListResultStatus.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.originalMeasurementPointListResultStatus) {
            filter = {
                ...filter,
                originalMeasurementPointListResultStatus: {
                    value: searchForm.value.originalMeasurementPointListResultStatus.value,
                    label: searchForm.value.originalMeasurementPointListResultStatus.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.building) {
            filter = {
                ...filter,
                building: {
                    value: searchForm.value.building.value,
                    label: searchForm.value.building.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.floor) {
            filter = {
                ...filter,
                floor: {
                    value: searchForm.value.floor.value,
                    label: searchForm.value.floor.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.location) {
            filter = {
                ...filter,
                location: {
                    value: searchForm.value.location.value,
                    label: searchForm.value.location.label
                }
            };
        }

        if (searchForm && searchForm.value && searchForm.value.mainCategory) {
            filter = {
                ...filter,
                mainCategory: {
                    value: searchForm.value.mainCategory.value,
                    label: searchForm.value.mainCategory.label
                }
            };
        }

        props.setTableFilter(filter);
    };

    const buildSearchControls = (): FieldConfig => {
        const {
            building,
            floor,
            location,
            measurementPointListResultStatus,
            workOrderResultStatus,
            mainCategory,
            system,
            originalMeasurementPointListResultStatus
        } = props.tableFilters;

        const disabled = false;
        /*
         * Greying matches the desktop toolbar (SearchInventoryTableForm): a level is only
         * selectable once its parent has been chosen. Without it a Floor can be picked with no
         * Building, which fills nothing in and reads as a broken filter.
         */
        const floorDisabled = !building;
        const locationDisabled = !floor;
        const foundBuilding = find(props.buildings, (b: Ibuilding) => {
            return building && b.id === building.value;
        });
        if (building && !foundBuilding) {
            props.clearTableFilter();
        }
        let floorOptions;
        let locationOptions;

        const buildingOptions = sortOptionsNatural(
            FormUtil.convertToOptions([...props.buildings, defaultBuildingID])
        );

        if (foundBuilding && typeof foundBuilding === 'object' && foundBuilding.floors) {
            floorOptions = sortOptionsNatural(FormUtil.convertToOptions(foundBuilding.floors));
            if (floor) {
                const foundFloor = find(foundBuilding.floors, (f: Ifloor) => {
                    return f.id === floor.value;
                });
                if (foundFloor) {
                    locationOptions = sortOptionsNatural(FormUtil.convertToOptions(foundFloor.locations));
                }
            }
        }

        const measurementPointListResultStatusOptions = [
            ...FormUtil.convertEnumToOptions(inventoryStatusFilterEnum)
        ].map(item => {
            return {
                value: item.value,
                label: props.t('manageInventory:' + item.label)
            };
        });
        const workOrderResultStatusOptions = FormUtil.convertEnumToOptions(workOrderStatusEnum).map(item => {
            return {
                value: item.value,
                label: props.t('manageWorkOrder:' + item.label)
            };
        });
        const originalStatusFilterOptions = FormUtil.convertEnumToOptions(inventoryStatusFilterEnum)
            // the original status filter matches by exact status, so the derived
            // "Not Tested Last 12M" option does not apply there
            .filter(item => item.value !== inventoryStatusFilterEnum.resultStatusNotTestedLast12M)
            .map(item => ({
                value: item.value,
                label: props.t('manageMeasurementPointLists:' + item.label)
            }))
            .sort((a, b) => getStatusOrder(a.value) - getStatusOrder(b.value));

        let mainSearchControls = {
            mainCategory: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'common:mainCategory',
                    options: props.productInfo.mainCategoryOptions,
                    colWidth: 5,
                    colWidthLarge: 4,
                    type: 'select',
                    placeholder: 'searchPlaceholder',
                    isClearable: true,
                    shouldTranslate: false
                },
                formState: { value: mainCategory, disabled }
            },
            measurementPointListResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'status',
                    options: measurementPointListResultStatusOptions,
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: 'manageInventory:measurementPointListResultStatusPlaceholder',
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: {
                    value: measurementPointListResultStatus,
                    disabled
                }
            },
            workOrderResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'manageWorkOrder:workOrderResultStatus',
                    options: workOrderResultStatusOptions,
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: 'manageWorkOrder:workOrderResultStatusPlaceholder',
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: { value: workOrderResultStatus, disabled }
            },
            originalMeasurementPointListResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'status',
                    options: originalStatusFilterOptions,
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: 'manageInventory:measurementPointListResultStatusPlaceholder',
                    isClearable: true,
                    shouldTranslate: true,
                    disableSort: true
                },
                formState: { value: originalMeasurementPointListResultStatus, disabled }
            },
            building: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'common:building',
                    options: buildingOptions,
                    colWidth: 5,
                    colWidthLarge: 4,
                    type: 'text',
                    placeholder: 'searchPlaceholder',
                    isClearable: true,
                    // the options are already in natural order; SelectWithoutValidation would
                    // otherwise re-sort them lexicographically and put 10 back in front of 2
                    disableSort: true
                },
                formState: { value: building, disabled }
            },
            floor: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'floor',
                    colWidth: 4,
                    colWidthLarge: 4,
                    placeholder: 'common:searchPlaceholder',
                    options: floorOptions,
                    isClearable: true,
                    disableSort: true,
                    disabled: floorDisabled
                },
                formState: { value: floor, disabled }
            }
        } as { [key: string]: GroupProps };

        let additionalSearchControls = {
            location: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'location',
                    colWidth: 4,
                    colWidthLarge: 2,
                    placeholder: 'common:searchPlaceholder',
                    options: locationOptions,
                    isClearable: true,
                    disableSort: true,
                    disabled: locationDisabled
                },
                formState: { value: location, disabled }
            },
            system: {
                render: FormUtil.Select,
                meta: {
                    options: constants.constructSystems(props.t),
                    label: 'manageInventory:system',
                    colWidth: 4,
                    placeholder: 'nsJob:typePlaceholder',
                    name: 'system',
                    disableSort: true,
                    isClearable: true
                },
                formState: { value: system, disabled }
            }
        } as { [key: string]: GroupProps };

        if (props.selectedJob.jobTypeID !== jobTypesIdEnum.agsRebalancing) {
            additionalSearchControls = omit(additionalSearchControls, 'system');
        }

        if (props.isAddWorkOrderMode === true) {
            // add-work-order mode (All Assets) filters on the status the asset had
            // before this job, matching the desktop Status dropdown
            mainSearchControls = omit(mainSearchControls, [
                'workOrderResultStatus',
                'measurementPointListResultStatus'
            ]);
            if (!shouldShowStatusFilter(true, props.isWorkOrderTypeJob, props.selectedJob.jobTypeID)) {
                mainSearchControls = omit(mainSearchControls, ['originalMeasurementPointListResultStatus']);
            }
        } else {
            mainSearchControls = omit(mainSearchControls, ['originalMeasurementPointListResultStatus']);
            if (props.isWorkOrderTypeJob) {
                mainSearchControls = omit(mainSearchControls, 'measurementPointListResultStatus');
            } else {
                mainSearchControls = omit(mainSearchControls, 'workOrderResultStatus');
            }
        }

        return { controls: { ...mainSearchControls, ...additionalSearchControls } };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        setShow(props.show);

        if (props.show) {
            setState({ ...state, fieldConfig: FormUtil.translateForm(buildSearchControls(), props.t) });
            // Seed the staged room multi-select from the committed filters.
            const { building, floor, location, rooms } = props.tableFilters;
            setRoomSelections(rooms || []);
            setRoomOptions(computeRoomOptions(building?.value, floor?.value, location?.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show]);

    /*
     * meta.disabled + stateChanges.next() is how this form already pushes new options into a
     * control; SelectWithoutValidation ORs meta.disabled with the control's own disabled flag, so
     * greying rides the same channel instead of a second mechanism.
     */
    const setControlDisabled = (key: string, isDisabled: boolean) => {
        const control = searchForm?.get(key) as any;
        if (control && control.meta) {
            control.meta.disabled = isDisabled;
            control.stateChanges.next();
        }
    };

    const filterFloors = (value: string | null) => {
        if (value) {
            const buildingID = value;
            const foundBuilding = find(props.buildings, (b: Ibuilding) => {
                return b.id === buildingID;
            });
            let newFloorOptions: any[] = [];

            if (foundBuilding) {
                newFloorOptions = FormUtil.convertToOptions(foundBuilding.floors);
            }
            const floorControl = searchForm?.get('floor') as any;
            floorControl.meta.options = newFloorOptions;
            floorControl.stateChanges.next();
        }
        const locationControl = searchForm?.get('location') as any;
        if (locationControl) {
            locationControl.meta.options = [];
            locationControl.stateChanges.next();
        }
        // Building changed — the room list no longer applies.
        setRoomOptions([]);
        setRoomSelections([]);

        // Floor opens only once a Building is chosen; Location waits for a Floor.
        setControlDisabled('floor', !value);
        setControlDisabled('location', true);

        (searchForm as FormGroup).patchValue({ floor: null, location: null });
    };

    const filterLocations = (value: string | null) => {
        /*
         * No early return on a cleared Floor. The old guard checked props.tableFilters.floor - the
         * *committed* filter - so clearing a Floor that had only been picked in this modal (never
         * applied) left Location enabled, holding a stale value and options from the old Floor.
         * Both subscriptions only fire on a real change, so cascading unconditionally is safe.
         */
        const { building } = searchForm.value;
        if (building && value && value) {
            const floorID = value;
            const foundBuilding = find(props.buildings, (b: Ibuilding) => {
                return b.id === building.value;
            });
            let newLocationOptions: any[] = [];

            if (foundBuilding) {
                const floor = find(foundBuilding.floors, (f: Ifloor) => {
                    return f.id === floorID;
                });
                if (floor) {
                    newLocationOptions = FormUtil.convertToOptions(floor.locations);
                }
            }

            const locationControl = searchForm.get('location') as any;
            if (locationControl) {
                locationControl.meta.options = newLocationOptions;
                locationControl.stateChanges.next();
            }
        }
        // Floor changed — the room list no longer applies.
        setRoomOptions([]);
        setRoomSelections([]);

        // Location follows Floor, whether the Floor was chosen or cleared.
        setControlDisabled('location', !value);

        (searchForm as FormGroup).patchValue({ location: null });
    };

    const filterRooms = (value: string | null) => {
        const { building, floor } = searchForm.value;
        // Location changed — recompute the room list and clear any prior room selections.
        setRoomOptions(computeRoomOptions(building?.value, floor?.value, value ?? undefined));
        setRoomSelections([]);
    };

    const onValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'building':
                filterFloors(value);
                break;
            case 'location':
                filterRooms(value);
                break;
            case 'floor':
                filterLocations(value);
                break;
            default:
                break;
        }
    };

    React.useEffect(() => {
        if (searchForm) {
            // Subscribe to value changes
            const buildingSubscription = searchForm.get('building')?.valueChanges.subscribe((value: Ioption) => {
                // value is null when the Building select is cleared; still cascade-reset dependents.
                onValueChanges(value ? value.value : null, 'building');
            }) as any;

            // null means the select was cleared; cascade anyway so Location/Room grey back out
            // (the Building subscription below has always done this).
            const floorSubscription = searchForm.get('floor')?.valueChanges.subscribe((value: Ioption) => {
                onValueChanges(value ? value.value : null, 'floor');
            }) as any;

            const locationSubscription = searchForm.get('location')?.valueChanges.subscribe((value: Ioption) => {
                onValueChanges(value ? value.value : null, 'location');
            }) as any;

            const measurementPointListResultStatusSubscription = searchForm
                .get('measurementPointListResultStatus')
                ?.valueChanges.subscribe((value: Ioption) => {
                    if (value !== null) {
                        onValueChanges(value.value, 'measurementPointListResultStatus');
                    }
                }) as any;

            const workOrderResultStatusSubscription = searchForm
                .get('workOrderResultStatus')
                ?.valueChanges.subscribe((value: Ioption) => {
                    if (value !== null) {
                        onValueChanges(value.value, 'workOrderResultStatus');
                    }
                }) as any;

            const originalMeasurementPointListResultStatusSubscription = searchForm
                .get('originalMeasurementPointListResultStatus')
                ?.valueChanges.subscribe((value: Ioption) => {
                    if (value !== null) {
                        onValueChanges(value.value, 'originalMeasurementPointListResultStatus');
                    }
                }) as any;

            const mainCategorySubscription = searchForm
                .get('mainCategory')
                ?.valueChanges.subscribe((value: Ioption) => {
                    if (value !== null) {
                        onValueChanges(value.value, 'mainCategory');
                    }
                }) as any;

            // Clean up the subscription when the component unmounts
            return () => {
                if (buildingSubscription) {
                    buildingSubscription.unsubscribe();
                }
                if (floorSubscription) {
                    floorSubscription.unsubscribe();
                }
                if (locationSubscription) {
                    locationSubscription.unsubscribe();
                }
                if (measurementPointListResultStatusSubscription) {
                    measurementPointListResultStatusSubscription.unsubscribe();
                }
                if (workOrderResultStatusSubscription) {
                    workOrderResultStatusSubscription.unsubscribe();
                }
                if (originalMeasurementPointListResultStatusSubscription) {
                    originalMeasurementPointListResultStatusSubscription.unsubscribe();
                }
                if (mainCategorySubscription) {
                    mainCategorySubscription.unsubscribe();
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchForm]);

    const setForm = (form: FormGroup | FormArray) => {
        form.meta = {};
        setSearchForm(form);
    };

    const onHideVirtualDecommissionedChange = (event: ChangeEvent<HTMLInputElement>) => {
        props.setTableFilter({ hideVirtualDecommissionedToggle: event.target.checked, page: 0 });
    };

    return (
        <div className="filter--table-modal-mobile">
            <Modal className="filter-table-modal-mobile" show={show} onHide={handleClose}>
                <Modal.Header>
                    <Modal.Title>{String(props.t('filterSearch'))}</Modal.Title>
                    <Button onClick={handleClose}>
                        <FontAwesomeIcon icon={['far', 'times']} size="lg" style={{ color: props.primaryColor }} />
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <form className="filter-asset-form-mobile">
                        <FormGenerator onMount={setForm} fieldConfig={state.fieldConfig} />
                    </form>
                    <div className={`mobile-room-filter${roomOptions.length === 0 ? ' disabled' : ''}`}>
                        <span className="mobile-room-filter-label">{String(props.t('manageInventory:room'))}</span>
                        <MultiSelectFilterList
                            values={roomSelections}
                            options={roomOptions}
                            onChange={setRoomSelections}
                            searchPlaceholder="Search rooms..."
                            emptyLabel="No rooms"
                        />
                    </div>
                    <HideVirtualMobileToggle
                        onHideVirtualChange={onHideVirtualDecommissionedChange}
                        checked={props.tableFilters.hideVirtualDecommissionedToggle || false}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={applyFilter}
                        style={{
                            backgroundColor: props.bgColor,
                            borderColor: props.brColor
                        }}
                    >
                        {String(props.t('applyFilters'))}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    return {};
};

export default withTranslation('nsJob')(connect(mapStateToProps, {})(FilterAssetTableModal));
